/**
 * GS1 Universal Parser — Backtracking + Scoring
 *
 * Porta fiel do offline (android_flutter_oficial/lib/core/utils/gtin/parsers/
 * gs1_universal_parser.dart). Resolve casos GS1-128/DataMatrix SEM separador GS
 * (FNC1), onde a heurística sequencial corta lote/serial numérico em posição
 * errada (ex.: lote "229180216" partido ao encontrar "21" no meio dos dígitos).
 *
 * Estratégia: busca em profundidade (backtracking) + validação de formato por AI
 * + scoring. Para cada AI variável testa tamanhos 1..max e escolhe a combinação
 * que (a) consome 100% da string com AIs válidos e (b) tem o maior score.
 *
 * IMPORTANTE: manter ESTE arquivo em paridade 1:1 com o Dart. Qualquer ajuste de
 * scoring/validação deve refletir no corpus comum (shared/gs1_corpus.json).
 */
import { getAILength, hasKnownAI, validateGtinChecksum } from './gs1Parser';

interface ParseResult {
  ais: Record<string, string>;
  consumedChars: number;
  score: number;
}

/** Penalty por AI extra — desincentiva fragmentação (lote len=1 + serial len=1 + AI 20). */
const FRAG_PENALTY = 4;

const RE_DIGITS = /^\d+$/;
const RE_AI82 = /^[A-Za-z0-9\-./!"%&+,:;<=>?_]+$/; // subset GS1 "82"

/** Parseia uma string GS1 normalizada (sem symbology identifier, GS→|).
 *  Retorna Map AI→valor, ou null se malformada. */
export function parseGS1Universal(data: string): Record<string, string> | null {
  if (!data) return null;
  const deduped = deduplicateIfExactCopy(data);
  const result = parseFrom(deduped, 0, null, false, false);
  if (result == null) return null;
  return result.ais;
}

/** Remove duplicação exata (firmware bugado envia o código 2x concatenado). */
function deduplicateIfExactCopy(data: string): string {
  if (data.length < 40) return data;
  if (data.length % 2 !== 0) return data;
  const half = data.length / 2;
  if (data.substring(0, half) !== data.substring(half)) return data;
  return data.substring(0, half);
}

function parseFrom(
  data: string,
  pos: number,
  prevAI: string | null,
  prevWasVariable: boolean,
  justAfterGS: boolean,
): ParseResult | null {
  // Caso base 1: consumimos tudo
  if (pos >= data.length) {
    return { ais: {}, consumedChars: pos, score: 0 };
  }

  // Caso base 2: char é | (GS separator), pula e marca justAfterGS=true
  if (data[pos] === '|') {
    return parseFrom(data, pos + 1, prevAI, prevWasVariable, true);
  }

  const ai = detectAI(data, pos);
  if (ai == null) return null; // malformada — nenhum AI válido aqui

  const afterAI = pos + ai.length;
  const fixedLen = getAILength(ai); // null/undefined = variável
  const currentIsVariable = fixedLen == null;

  // REGRA ESTRUTURAL (GS1 spec 7.11): dois AIs variáveis adjacentes sem FNC1
  // só no par 10↔21 (empírico HID Medtronic/J&J). Demais pares = falso positivo.
  if (prevWasVariable && currentIsVariable && !justAfterGS) {
    if (!isAllowedAdjacentVariablePair(prevAI as string, ai)) {
      return null;
    }
  }

  if (fixedLen != null) {
    return parseFixed(data, ai, afterAI, fixedLen);
  }
  return parseVariable(data, ai, afterAI);
}

function isAllowedAdjacentVariablePair(prev: string, curr: string): boolean {
  if (prev === '10' && curr === '21') return true;
  if (prev === '21' && curr === '10') return true;
  return false;
}

function parseFixed(data: string, ai: string, afterAI: number, fixedLen: number): ParseResult | null {
  if (afterAI + fixedLen > data.length) return null; // truncada
  const value = data.substring(afterAI, afterAI + fixedLen);
  if (!validateFormat(ai, value)) return null;

  const rest = parseFrom(data, afterAI + fixedLen, ai, false, false);
  if (rest == null) return null;

  const newAis: Record<string, string> = { [ai]: value, ...rest.ais };
  const score = scoreFixed(ai) + rest.score - FRAG_PENALTY;
  return { ais: newAis, consumedChars: rest.consumedChars, score };
}

function parseVariable(data: string, ai: string, afterAI: number): ParseResult | null {
  // 1. GS (|) explícito como delimitador definitivo
  const gsPos = data.indexOf('|', afterAI);
  if (gsPos !== -1) {
    const value = data.substring(afterAI, gsPos);
    if (validateFormat(ai, value)) {
      const rest = parseFrom(data, gsPos + 1, ai, true, true);
      if (rest != null) {
        const newAis: Record<string, string> = { [ai]: value, ...rest.ais };
        const score = scoreVariableWithGS() + rest.score - FRAG_PENALTY;
        return { ais: newAis, consumedChars: rest.consumedChars, score };
      }
    }
    // se o GS explícito não deu parse válido, cai no fallback de tamanhos
  }

  // 2. Sem GS válido: tentar tamanhos 1..min(max, restante) e escolher o melhor score
  let best: ParseResult | null = null;
  const maxLen = data.length - afterAI;
  const maxTry = maxLen > maxVariableLen(ai) ? maxVariableLen(ai) : maxLen;

  for (let len = 1; len <= maxTry; len++) {
    const value = data.substring(afterAI, afterAI + len);
    if (!validateFormat(ai, value)) continue;

    const rest = parseFrom(data, afterAI + len, ai, true, false);
    if (rest == null) continue;

    const totalScore = scoreVariableNoGS(ai, len) + rest.score - FRAG_PENALTY;
    if (best == null || totalScore > best.score) {
      const newAis: Record<string, string> = { [ai]: value, ...rest.ais };
      best = { ais: newAis, consumedChars: rest.consumedChars, score: totalScore };
    }
  }

  return best;
}

function maxVariableLen(ai: string): number {
  switch (ai) {
    case '10':
    case '21':
    case '22':
    case '30':
    case '37':
      return 20;
    case '240':
    case '241':
    case '242':
    case '243':
    case '250':
    case '251':
    case '253':
    case '8001':
    case '8003':
    case '8004':
    case '8005':
    case '8017':
    case '8018':
      return 30;
    case '8200':
      return 70;
    default:
      return 30;
  }
}

/** Detecta o AI na posição (tenta 4, 3, 2 dígitos; 100% numérico; conhecido). */
function detectAI(data: string, pos: number): string | null {
  for (const len of [4, 3, 2]) {
    if (pos + len > data.length) continue;
    const candidate = data.substring(pos, pos + len);
    if (!RE_DIGITS.test(candidate)) continue;
    if (hasKnownAI(candidate)) return candidate;
  }
  return null;
}

function validateFormat(ai: string, value: string): boolean {
  if (!value) return false;

  switch (ai) {
    case '01':
    case '02':
      if (value.length !== 14) return false;
      if (!/^\d{14}$/.test(value)) return false;
      return validateGtinChecksum(value);

    case '11':
    case '12':
    case '13':
    case '15':
    case '16':
    case '17': {
      if (value.length !== 6) return false;
      if (!/^\d{6}$/.test(value)) return false;
      const mm = parseInt(value.substring(2, 4), 10);
      const dd = parseInt(value.substring(4, 6), 10);
      if (isNaN(mm) || mm < 1 || mm > 12) return false;
      if (isNaN(dd) || dd < 0 || dd > 31) return false;
      return true;
    }

    case '20':
      return value.length === 2 && /^\d{2}$/.test(value);

    case '10':
    case '21':
    case '22':
      if (value.length < 1 || value.length > 20) return false;
      return RE_AI82.test(value);

    case '240':
    case '241':
    case '242':
    case '243':
    case '250':
    case '251':
    case '253':
      if (value.length < 1 || value.length > 30) return false;
      return RE_AI82.test(value);

    case '30':
    case '37':
      if (value.length < 1 || value.length > 8) return false;
      return /^\d+$/.test(value);

    case '00':
      return value.length === 18 && /^\d{18}$/.test(value);

    case '410':
    case '411':
    case '412':
    case '413':
    case '414':
    case '415':
    case '416':
    case '417':
      return value.length === 13 && /^\d{13}$/.test(value);

    case '422':
    case '424':
    case '426':
      return value.length === 3 && /^\d{3}$/.test(value);

    case '7001':
      return value.length === 13 && /^\d{13}$/.test(value);
    case '7003':
      return value.length === 10 && /^\d{10}$/.test(value);
    case '8001':
      return value.length === 14 && /^\d{14}$/.test(value);
    case '8005':
      return value.length === 6 && /^\d{6}$/.test(value);
    case '8006':
    case '8017':
    case '8018':
    case '8026':
      return value.length === 18 && /^\d{18}$/.test(value);
    case '8111':
      return value.length === 4 && /^\d{4}$/.test(value);

    default:
      // AIs de peso/medida (310-369): 6 dígitos
      if (/^3[1-6]\d$/.test(ai)) {
        return value.length === 6 && /^\d{6}$/.test(value);
      }
      // AI conhecido sem validação específica: 1-30 chars
      return value.length >= 1 && value.length <= 30;
  }
}

function scoreFixed(ai: string): number {
  switch (ai) {
    case '01':
    case '02':
      return 30;
    case '17':
      return 28;
    case '11':
      return 25;
    case '15':
      return 22;
    case '20':
      return 25;
    case '00':
      return 22;
    case '13':
      return 10;
    case '12':
      return 5;
    case '16':
      return 5;
    case '410':
    case '411':
    case '412':
    case '413':
    case '414':
      return 15;
    default:
      // AIs de peso/medida (3[1-6]x): raros em medical devices → score baixo
      if (ai.length === 3 && /^3[1-6]\d$/.test(ai)) return 5;
      return 15;
  }
}

function scoreVariableWithGS(): number {
  return 40;
}

function scoreVariableNoGS(ai: string, len: number): number {
  switch (ai) {
    case '10': // lote
      if (len >= 8 && len <= 12) return 38;
      if (len >= 6 && len <= 7) return 18;
      if (len >= 13 && len <= 15) return 22;
      if (len >= 16 && len <= 20) return 12;
      if (len >= 4 && len <= 5) return 5;
      if (len === 3) return 1;
      return 0;

    case '21': // serial
      if (len >= 8 && len <= 14) return 28;
      if (len >= 15 && len <= 18) return 55;
      if (len >= 6 && len <= 7) return 22;
      if (len >= 19 && len <= 20) return 35;
      if (len >= 4 && len <= 5) return 10;
      if (len === 3) return 2;
      return 0;

    case '22':
      if (len >= 6 && len <= 20) return 15;
      if (len >= 3 && len <= 5) return 5;
      return 0;

    case '240':
    case '241':
    case '250':
    case '251':
      if (len >= 6 && len <= 12) return 20;
      if (len >= 4 && len <= 5) return 15;
      if (len >= 13 && len <= 15) return 15;
      if (len >= 16 && len <= 30) return 8;
      if (len >= 2 && len <= 3) return 2;
      return 0;

    case '30':
    case '37':
      if (len >= 1 && len <= 8) return 10;
      return 3;

    default:
      return 5;
  }
}
